import { useState, useEffect } from 'react';
import { Plus, Trash2, Bus, Car } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { getAdminVehicles, createVehicle, deleteVehicle } from '../../lib/api';
import { toast } from 'sonner';
import AdminLayout from '../../components/AdminLayout';

const VEHICLE_TYPES = [
  { value: 'bus', label: 'Bus', icon: Bus },
  { value: 'coach', label: 'Coach', icon: Bus },
  { value: 'taxi', label: 'Taxi', icon: Car },
];

export default function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    registration: '',
    make: '',
    model: '',
    vehicle_type: 'bus'
  });

  useEffect(() => {
    loadVehicles();
  }, []);

  const loadVehicles = async () => {
    try {
      const response = await getAdminVehicles();
      setVehicles(response.data);
    } catch (error) {
      toast.error('Failed to load vehicles');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      registration: '',
      make: '',
      model: '',
      vehicle_type: 'bus'
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.registration.trim() || !formData.make.trim() || !formData.model.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    setSaving(true);
    try {
      await createVehicle(formData);
      toast.success('Vehicle added successfully');
      setShowForm(false);
      resetForm();
      loadVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add vehicle');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    try {
      await deleteVehicle(deleteTarget.id);
      toast.success('Vehicle deleted');
      loadVehicles();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    } finally {
      setDeleteTarget(null);
    }
  };

  const getVehicleIcon = (type) => {
    const found = VEHICLE_TYPES.find(t => t.value === type);
    return found ? found.icon : Bus;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="vehicles-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-blue-900">Vehicles</h1>
            <p className="text-slate-500">Manage your fleet</p>
          </div>
          <Button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="bg-yellow-500 hover:bg-yellow-600 text-blue-950 font-bold btn-press"
            data-testid="add-vehicle-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Vehicle
          </Button>
        </div>

        {/* Vehicles List */}
        {vehicles.length === 0 ? (
          <div className="empty-state" data-testid="empty-vehicles">
            <Bus className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <p className="text-lg font-medium text-slate-600">No vehicles added yet</p>
            <p className="text-slate-400 mb-4">Add your first vehicle to get started</p>
            <Button
              onClick={() => { resetForm(); setShowForm(true); }}
              className="bg-yellow-500 hover:bg-yellow-600 text-blue-950"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Vehicle
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="vehicles-list">
            {vehicles.map((vehicle, idx) => {
              const Icon = getVehicleIcon(vehicle.vehicle_type);
              return (
                <Card key={vehicle.id} className="card-hover border-slate-200" data-testid={`vehicle-card-${idx}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                          <Icon className="w-6 h-6 text-yellow-600" />
                        </div>
                        <div>
                          <h3 className="font-mono font-bold text-xl text-blue-900">
                            {vehicle.registration}
                          </h3>
                          <p className="text-sm text-slate-500">
                            {vehicle.make} {vehicle.model}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                      <Badge className="bg-blue-100 text-blue-800 capitalize">
                        {vehicle.vehicle_type}
                      </Badge>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteTarget(vehicle)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-vehicle-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add Vehicle Dialog */}
        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add New Vehicle</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="form-group">
                <Label htmlFor="registration">Registration *</Label>
                <Input
                  id="registration"
                  value={formData.registration}
                  onChange={(e) => setFormData({ ...formData, registration: e.target.value.toUpperCase() })}
                  placeholder="AB12 CDE"
                  className="font-mono"
                  data-testid="vehicle-reg-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="make">Make *</Label>
                <Input
                  id="make"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  placeholder="Mercedes"
                  data-testid="vehicle-make-input"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="model">Model *</Label>
                <Input
                  id="model"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  placeholder="Sprinter"
                  data-testid="vehicle-model-input"
                />
              </div>

              <div className="form-group">
                <Label>Vehicle Type *</Label>
                <Select
                  value={formData.vehicle_type}
                  onValueChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                >
                  <SelectTrigger data-testid="vehicle-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {VEHICLE_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-yellow-500 hover:bg-yellow-600 text-blue-950 font-bold"
                  data-testid="save-vehicle-btn"
                >
                  {saving ? 'Saving...' : 'Add Vehicle'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Vehicle?</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{deleteTarget?.registration}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
